import React, { useState, useRef, useEffect } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'services/useClickOutside';

// ##### TimePicker — copied from planner/moduleComponents/TimePicker.jsx (2026-08-12).
// Travel needs its own hour input (flight/checkin times) but has no cross-module
// import convention in this repo, so this is a local copy, not a shared_items move.
// Simplified vs. the Planner original: single time only (no range), always 24h
// (travel logistics don't need AM/PM), no timeFormatPreference dependency.
const TimePicker = ({
  value = '', // 'HH:MM' or ''
  onChange,
  name = 'time',
  disabled = false,
  required = false,
  className = '',
  placeholder = 'Select time',
  /** Z-index for the time portal (e.g. 80 when inside a z-70 modal). */
  dropdownZIndex = 50,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(0);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [activeSection, setActiveSection] = useState('hour');
  const [errorMessage, setErrorMessage] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      if (value && typeof value === 'string') {
        const [hours, minutes] = value.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          setSelectedHour(hours);
          setSelectedMinute(minutes);
        }
      } else {
        setSelectedHour(0);
        setSelectedMinute(0);
        setActiveSection('hour');
      }
    }
  }, [isOpen, value]);

  const formatSingleTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const isValidHour = (hour) => !isNaN(hour) && hour >= 0 && hour <= 23;
  const isValidMinute = (minute) => !isNaN(minute) && minute >= 0 && minute <= 59;

  const handleHourChange = (e) => {
    e.stopPropagation();
    const v = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedHour(isNaN(v) ? '' : v);
    setErrorMessage(null);
  };

  const handleMinuteChange = (e) => {
    e.stopPropagation();
    const v = e.target.value === '' ? '' : parseInt(e.target.value);
    setSelectedMinute(isNaN(v) ? '' : v);
    setErrorMessage(null);
  };

  const handleApply = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }

    if (!isValidHour(selectedHour)) {
      setErrorMessage('Invalid hour');
      return;
    }
    if (!isValidMinute(selectedMinute)) {
      setErrorMessage('Invalid minute');
      return;
    }

    const timeValue = `${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;
    if (onChange) {
      onChange({ target: { name: name, value: timeValue } });
    }
    setErrorMessage(null);
    setIsOpen(false);
  };

  const getPortalStyle = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      zIndex: dropdownZIndex,
    };
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors ${className}`}
      >
        <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className={`text-xs ${value ? 'text-gray-800' : 'text-gray-400'}`}>
          {formatSingleTime(value) || placeholder}
        </span>
      </button>

      {isOpen && createPortal(
        <div
          data-qapp-portal="timepicker"
          style={getPortalStyle()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            ref={dropdownRef}
            className="bg-white rounded-lg shadow-xl border border-gray-200 p-2.5 w-48"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center gap-1 text-xl font-semibold text-gray-800 mb-2">
              <div
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActiveSection('hour'); }}
                className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                  activeSection === 'hour' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'
                }`}
              >
                <input
                  type="number"
                  value={selectedHour}
                  onChange={handleHourChange}
                  onFocus={() => setActiveSection('hour')}
                  className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <span className="text-gray-400">:</span>
              <div
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActiveSection('minute'); }}
                className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                  activeSection === 'minute' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'
                }`}
              >
                <input
                  type="number"
                  value={selectedMinute}
                  onChange={handleMinuteChange}
                  onFocus={() => setActiveSection('minute')}
                  className="w-10 text-center bg-transparent border-none outline-none text-xl font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            {errorMessage && (
              <div className="mb-2 text-xs text-red-600 font-medium text-center">{errorMessage}</div>
            )}

            <button
              type="button"
              onClick={handleApply}
              className="w-full px-2 py-1 text-xs bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors font-medium"
            >
              Apply
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimePicker;
